import { motion } from 'framer-motion';
import { Banknote, CircleAlert, Lock, type LucideIcon } from 'lucide-react';
import { usePaymentSummary } from '@/lib/queries/payments';
import { cn } from '@/lib/utils';
import { useHasPermission } from '@/stores/auth-store';

type Segment = {
  key: string;
  label: string;
  /** What the number counts, shown under it. */
  legend: string;
  value: number | undefined;
  icon: LucideIcon;
  tone: string;
};

/**
 * Today's settlement standing beside the fleet counts: who has paid their daily
 * rate, who has not, and how many vehicles are actually held.
 *
 * Deliberately not a filter. The four status cards above it drive the map, and
 * these three are driver-account facts, so making them look clickable would
 * promise a filter the map cannot honour.
 */
export function FleetComplianceStrip({
  immobilised,
  className,
}: {
  immobilised: number;
  className?: string;
}) {
  const canReadPayments = useHasPermission('payment:read');
  const { data: summary } = usePaymentSummary(canReadPayments);

  const segments: Segment[] = [
    ...(canReadPayments
      ? [
          {
            key: 'paid',
            label: 'Paid today',
            legend: "Paid today's rate in full",
            value: summary?.driversPaidToday,
            icon: Banknote,
            tone: 'bg-moving-soft text-moving',
          },
          {
            key: 'unpaid',
            label: 'Unpaid',
            legend: 'Still owing for today',
            value: summary?.driversUnpaidToday,
            icon: CircleAlert,
            tone: 'bg-idle-soft text-idle',
          },
        ]
      : []),
    {
      key: 'immobilised',
      label: 'Immobilised',
      legend: 'Engine cut confirmed',
      value: immobilised,
      icon: Lock,
      tone: 'bg-alert-soft text-alert',
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.26, delay: 0.16, ease: [0.2, 0.8, 0.2, 1] }}
      className={cn(
        'pointer-events-auto flex w-fit max-w-full flex-wrap items-center gap-x-1 gap-y-2 rounded-xl bg-card/95 px-3.5 py-2.5 shadow-panel backdrop-blur',
        className,
      )}
    >
      {segments.map((segment, index) => (
        <div key={segment.key} className="flex items-center">
          {index > 0 && <span aria-hidden className="mr-3 h-7 w-px bg-border" />}
          <span
            className={cn(
              'mr-2 flex size-6 shrink-0 items-center justify-center rounded-md',
              segment.tone,
            )}
          >
            <segment.icon className="size-3.5" strokeWidth={2.1} />
          </span>
          <span className="min-w-0 pr-2">
            <span className="block truncate text-[11.5px] leading-tight font-medium text-muted-foreground">
              {segment.label}
            </span>
            <span className="tnum block text-base leading-tight font-bold">
              {segment.value ?? '—'}
            </span>
            {/* Left out on phones, where the strip already wraps under the status cards. */}
            <span className="hidden max-w-[150px] truncate text-[10.5px] leading-tight text-muted-foreground sm:block" title={segment.legend}>
              {segment.legend}
            </span>
          </span>
        </div>
      ))}
    </motion.div>
  );
}
